import { useState } from "react";
import { BRAND } from "@/data/content";
import { Instagram, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      trackEvent("newsletter_subscribe");
      toast.success("Subscribed. Welcome to NextGen.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Subscription failed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ng-bg border-t border-ng-line pt-20 pb-10 hex-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="font-display italic font-black text-3xl tracking-tight leading-none flex">
                <span className="chrome-text">{BRAND.short_split.silver}</span>
                <span className="electric-text">{BRAND.short_split.blue}</span>
              </span>
              <span className="font-display text-[10px] tracking-[0.35em] text-ng-blue uppercase border-l border-ng-line pl-3">
                Performance
              </span>
            </div>
            <p className="mt-5 text-white/65 font-light max-w-sm leading-relaxed">
              {BRAND.sub_tagline}
            </p>
            <p className="mt-2 text-ng-blue text-sm font-display font-semibold uppercase tracking-[0.18em]">
              {BRAND.tagline}
            </p>

            <form onSubmit={onSubscribe} className="mt-8 max-w-md">
              <div className="label-eyebrow mb-3">Subscribe to the lab notes</div>
              <div className="flex border-b border-ng-line focus-within:border-ng-blue transition-colors">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                  className="flex-1 bg-transparent text-white py-3 focus:outline-none placeholder:text-white/30"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={loading}
                  className="px-4 text-ng-blue text-xs uppercase tracking-[0.22em] font-bold font-display disabled:opacity-60"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : "Join →"}
                </button>
              </div>
            </form>
          </div>

          <div className="md:col-span-3">
            <div className="label-eyebrow mb-5">Contact Coach J</div>
            <ul className="space-y-3 text-white/70 text-sm font-light">
              <li className="flex items-center gap-3">
                <Phone size={14} strokeWidth={1.5} className="text-ng-blue" />
                <a href={`tel:${BRAND.phone_link}`} className="hover:text-ng-blue transition-colors font-display font-semibold">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={14} strokeWidth={1.5} className="text-ng-blue" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-ng-blue transition-colors">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram size={14} strokeWidth={1.5} className="text-ng-blue" />
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-ng-blue transition-colors"
                >
                  {BRAND.instagram}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={14} strokeWidth={1.5} className="text-ng-blue mt-0.5" />
                <span>
                  {BRAND.city}
                  <br />
                  <span className="text-white/40 text-xs">{BRAND.region}</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="label-eyebrow mb-5">Navigate</div>
            <ul className="grid grid-cols-2 gap-2 text-white/70 text-sm font-light">
              {["Method", "Programs", "Peptides", "Coach", "Results", "FAQ", "Contact"].map(
                (l) => (
                  <li key={l}>
                    <a
                      href={`#${l.toLowerCase()}`}
                      className="hover:text-ng-blue transition-colors font-display tracking-wider uppercase text-xs"
                    >
                      {l}
                    </a>
                  </li>
                )
              )}
            </ul>

            <div className="mt-8 border border-yellow-500/30 bg-yellow-500/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-display font-bold">
                Peptide Compliance
              </div>
              <p className="mt-1 text-white/50 text-[11px] leading-relaxed">
                NextGen Pro Peptides products are FOR RESEARCH USE ONLY. Not for human or animal consumption. Not intended for medical or therapeutic use.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-ng-line flex flex-col md:flex-row gap-4 justify-between items-start md:items-center text-white/40 text-xs">
          <div>
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-ng-blue transition-colors uppercase tracking-widest font-display">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-ng-blue transition-colors uppercase tracking-widest font-display">Terms</Link>
            <Link to="/medical-disclaimer" data-testid="footer-medical" className="hover:text-ng-blue transition-colors uppercase tracking-widest font-display">Medical Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
